import { create } from 'zustand';

const useStore = create((set, get) => ({
  // Auth state
  user: JSON.parse(localStorage.getItem('user') || 'null'),
  token: localStorage.getItem('token'),
  isAuthenticated: !!localStorage.getItem('token'),

  // Company state
  companies: JSON.parse(localStorage.getItem('companies') || '[]'),
  currentCompany: JSON.parse(localStorage.getItem('currentCompany') || 'null'),

  setAuth: (user, token, companies = []) => {
    localStorage.setItem('user', JSON.stringify(user));
    localStorage.setItem('token', token);
    localStorage.setItem('companies', JSON.stringify(companies));

    // Set current company to first company if not already set
    const storedCompany = localStorage.getItem('currentCompany');
    const currentCompany = storedCompany ? JSON.parse(storedCompany) : (companies[0] || null);
    if (currentCompany) {
      localStorage.setItem('currentCompany', JSON.stringify(currentCompany));
    }

    set({ user, token, isAuthenticated: true, companies, currentCompany });
  },

  setCompanies: (companies) => {
    localStorage.setItem('companies', JSON.stringify(companies));
    set({ companies });
  },

  setCurrentCompany: (company) => {
    localStorage.setItem('currentCompany', JSON.stringify(company));
    set({
      currentCompany: company,
      // Clear dashboards when changing companies
      dashboards: [],
      currentDashboard: null,
      currentDashboardId: null
    });
    localStorage.removeItem('currentDashboardId');
  },

  logout: () => {
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    localStorage.removeItem('companies');
    localStorage.removeItem('currentCompany');
    localStorage.removeItem('currentDashboardId');
    set({
      user: null,
      token: null,
      isAuthenticated: false,
      companies: [],
      currentCompany: null,
      dashboards: [],
      currentDashboard: null,
      currentDashboardId: null
    });
  },

  // Metrics state
  metrics: {},
  metricDefinitions: [],

  setMetrics: (metrics) => set({ metrics }),

  updateMetric: (metricKey, data) => {
    set((state) => ({
      metrics: {
        ...state.metrics,
        [metricKey]: data,
      },
    }));
  },

  setMetricDefinitions: (definitions) => set({ metricDefinitions: definitions }),

  // Dashboard state
  dashboards: [],
  currentDashboard: null,
  currentDashboardId: localStorage.getItem('currentDashboardId'),

  setDashboards: (dashboards) => set({ dashboards }),

  setCurrentDashboard: (dashboard) => {
    if (dashboard && dashboard.id) {
      localStorage.setItem('currentDashboardId', dashboard.id);
    }
    set({ currentDashboard: dashboard, currentDashboardId: dashboard?.id });
  },

  addDashboard: (dashboard) => {
    set((state) => ({
      dashboards: [...state.dashboards, dashboard],
    }));
  },

  updateDashboard: (dashboardId, updates) => {
    set((state) => ({
      dashboards: state.dashboards.map((d) =>
        d.id === dashboardId ? { ...d, ...updates } : d
      ),
      currentDashboard:
        state.currentDashboard?.id === dashboardId
          ? { ...state.currentDashboard, ...updates }
          : state.currentDashboard,
    }));
  },

  deleteDashboard: (dashboardId) => {
    set((state) => ({
      dashboards: state.dashboards.filter((d) => d.id !== dashboardId),
      currentDashboard:
        state.currentDashboard?.id === dashboardId
          ? null
          : state.currentDashboard,
    }));
  },

  // WebSocket state
  wsConnected: false,
  setWsConnected: (connected) => set({ wsConnected: connected }),

  // Last update time per dashboard (dashboardId => timestamp)
  dashboardLastUpdates: {},
  setDashboardLastUpdate: (dashboardId, time) => {
    set((state) => ({
      dashboardLastUpdates: {
        ...state.dashboardLastUpdates,
        [dashboardId]: time,
      },
    }));
  },

  // Legacy support - keep for backwards compatibility
  lastUpdate: null,
  setLastUpdate: (time) => {
    const currentDashboardId = get().currentDashboard?.id;
    if (currentDashboardId) {
      get().setDashboardLastUpdate(currentDashboardId, time);
    }
    set({ lastUpdate: time });
  },

  // Loading states
  isLoading: false,
  setLoading: (loading) => set({ isLoading: loading }),

  // Edit mode for dashboard
  isEditMode: false,
  setEditMode: (editMode) => set({ isEditMode: editMode }),

  // User preferences
  animationsEnabled: localStorage.getItem('animationsEnabled') !== 'false', // default true
  setAnimationsEnabled: (enabled) => {
    localStorage.setItem('animationsEnabled', enabled);
    set({ animationsEnabled: enabled });
  },
}));

export default useStore;
